import React, { useRef, useState } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import toast from "react-hot-toast";
import { thumbUrl, uploadMedia } from "./artGalleryApi";

export const Field = ({ label, hint, children, className = "" }) => (
  <label className={`ag-field ${className}`}>
    {label && <span className="ag-field-label">{label}</span>}
    {children}
    {hint && <span className="ag-field-hint">{hint}</span>}
  </label>
);

export const TextField = ({ label, hint, value, onChange, multiline, rows = 3, ...rest }) => (
  <Field label={label} hint={hint}>
    {multiline ? (
      <textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...rest} />
    ) : (
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...rest} />
    )}
  </Field>
);

export const Toggle = ({ label, hint, checked, onChange }) => (
  <label className="ag-toggle">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
    <span className="ag-toggle-track" />
    <span>
      {label}
      {hint && <small>{hint}</small>}
    </span>
  </label>
);

export const Segmented = ({ options, value, onChange, full }) => (
  <div className={`ag-segmented ${full ? "ag-segmented-full" : ""}`}>
    {options.map((o) => (
      <button key={String(o.id)} type="button" className={value === o.id ? `is-active is-${o.id}` : ""} onClick={() => onChange(o.id)}>
        {o.label}
      </button>
    ))}
  </div>
);

export const RichText = ({ label, hint, value, onChange }) => (
  <div className="ag-field">
    {label && <span className="ag-field-label">{label}</span>}
    <div className="ag-richtext">
      <CKEditor
        editor={ClassicEditor}
        data={value ?? ""}
        config={{ toolbar: ["heading", "|", "bold", "italic", "link", "bulletedList", "numberedList", "|", "blockQuote", "|", "undo", "redo"] }}
        onChange={(event, editor) => onChange(editor.getData())}
      />
    </div>
    {hint && <span className="ag-field-hint">{hint}</span>}
  </div>
);

const pickFiles = (inputRef) => inputRef.current?.click();

export const ImageField = ({ label, hint, value, onChange, upload = uploadMedia }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const stored = await upload(file);
      if (onChange) onChange(stored);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="ag-field">
      {label && <span className="ag-field-label">{label}</span>}
      <div className="ag-image-field">
        <div className="ag-image-field-preview">{value?.url ? <img src={thumbUrl(value.url, 480)} alt="" /> : <span>No image</span>}</div>
        <div className="ag-image-field-actions">
          <button type="button" className="ag-btn ag-btn-ghost" onClick={() => pickFiles(inputRef)} disabled={busy}>
            {busy ? "Uploading..." : value?.url ? "Replace" : "Upload"}
          </button>
          {value?.url && onChange && (
            <button type="button" className="ag-btn ag-btn-danger-ghost" onClick={() => onChange(null)} disabled={busy}>
              Remove
            </button>
          )}
        </div>
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => handleFile(e.target.files[0])} />
      {hint && <span className="ag-field-hint">{hint}</span>}
    </div>
  );
};

const move = (list, from, to) => {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

export const GalleryField = ({ label, hint, value, onChange }) => {
  const images = Array.isArray(value) ? value : [];
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFiles = async (files) => {
    if (!files.length) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const file of files) uploaded.push({ ...(await uploadMedia(file)), alt: "" });
      onChange([...images, ...uploaded]);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="ag-field">
      {label && <span className="ag-field-label">{label}</span>}
      <div className="ag-gallery-field">
        {images.map((image, index) => (
          <div className="ag-gallery-item" key={image.url}>
            <img src={thumbUrl(image.url, 240)} alt="" />
            <input
              placeholder="Caption / alt text"
              value={image.alt || ""}
              onChange={(e) => onChange(images.map((img, i) => (i === index ? { ...img, alt: e.target.value } : img)))}
            />
            <div className="ag-gallery-item-actions">
              <button type="button" disabled={index === 0} onClick={() => onChange(move(images, index, index - 1))} aria-label="Move left">
                ←
              </button>
              <button type="button" disabled={index === images.length - 1} onClick={() => onChange(move(images, index, index + 1))} aria-label="Move right">
                →
              </button>
              <button type="button" onClick={() => onChange(images.filter((_, i) => i !== index))} aria-label="Remove">
                ✕
              </button>
            </div>
          </div>
        ))}
        <button type="button" className="ag-gallery-add" onClick={() => pickFiles(inputRef)} disabled={busy}>
          {busy ? "Uploading..." : "+ Add images"}
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        hidden
        onChange={(e) => handleFiles(Array.from(e.target.files || []))}
      />
      {hint && <span className="ag-field-hint">{hint}</span>}
    </div>
  );
};

export const StringList = ({ label, hint, value, onChange, placeholder }) => {
  const items = Array.isArray(value) ? value : [];
  return (
    <div className="ag-field">
      {label && <span className="ag-field-label">{label}</span>}
      <div className="ag-list-field">
        {items.map((item, index) => (
          <div className="ag-list-row" key={index}>
            <input value={item} placeholder={placeholder} onChange={(e) => onChange(items.map((v, i) => (i === index ? e.target.value : v)))} />
            <button type="button" className="ag-icon-btn" onClick={() => onChange(items.filter((_, i) => i !== index))} aria-label="Remove">
              ✕
            </button>
          </div>
        ))}
        <button type="button" className="ag-link-btn" onClick={() => onChange([...items, ""])}>
          + Add
        </button>
      </div>
      {hint && <span className="ag-field-hint">{hint}</span>}
    </div>
  );
};

// list of objects, each edited with the same sub-fields
export const Repeater = ({ label, hint, value, onChange, fields, itemLabel = "Item" }) => {
  const items = Array.isArray(value) ? value : [];
  const update = (index, key, next) => onChange(items.map((item, i) => (i === index ? { ...item, [key]: next } : item)));
  return (
    <div className="ag-field">
      {label && <span className="ag-field-label">{label}</span>}
      <div className="ag-repeater">
        {items.map((item, index) => (
          <div className="ag-repeater-item" key={index}>
            <div className="ag-repeater-head">
              <strong>
                {itemLabel} {index + 1}
              </strong>
              <span>
                <button type="button" className="ag-icon-btn" disabled={index === 0} onClick={() => onChange(move(items, index, index - 1))} aria-label="Move up">
                  ↑
                </button>
                <button
                  type="button"
                  className="ag-icon-btn"
                  disabled={index === items.length - 1}
                  onClick={() => onChange(move(items, index, index + 1))}
                  aria-label="Move down"
                >
                  ↓
                </button>
                <button type="button" className="ag-icon-btn" onClick={() => onChange(items.filter((_, i) => i !== index))} aria-label="Remove">
                  ✕
                </button>
              </span>
            </div>
            {fields.map((f) => (
              <TextField key={f.key} label={f.label} value={item[f.key]} multiline={f.multiline} rows={2} onChange={(v) => update(index, f.key, v)} />
            ))}
          </div>
        ))}
        <button type="button" className="ag-link-btn" onClick={() => onChange([...items, Object.fromEntries(fields.map((f) => [f.key, ""]))])}>
          + Add {itemLabel.toLowerCase()}
        </button>
      </div>
      {hint && <span className="ag-field-hint">{hint}</span>}
    </div>
  );
};
